import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, MapPin } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 pb-16 pt-10 sm:px-8 lg:grid-cols-12 lg:gap-6 lg:pb-24 lg:pt-16">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#E8D8D3] bg-white/70 px-4 py-2"
            data-testid="hero-licensed-badge"
          >
            <MapPin size={13} className="text-[#C48B71]" strokeWidth={1.5} />
            <span className="font-accent text-[11px] font-semibold uppercase tracking-[0.22em] text-[#5C4B47]">
              Licensed Bakery &bull; San Diego, CA
            </span>
          </motion.div>

          <h1
            className="font-display text-5xl font-light leading-[1.02] tracking-tight text-[#2D2422] sm:text-6xl lg:text-[5.2rem]"
            data-testid="hero-headline"
          >
            {["Custom Cakes", "Made for", "Your Moment"].map((l, i) => (
              <span key={l} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {i === 2 ? <em className="text-[#C48B71]">{l}</em> : l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-7 max-w-md text-base leading-relaxed text-[#5C4B47] sm:text-lg"
            data-testid="hero-subcopy"
          >
            Beautifully handcrafted cakes and desserts created for birthdays, celebrations,
            showers, weddings, and the moments worth remembering.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button onClick={() => scrollToId("#inquiry")} className="btn-primary" data-testid="hero-request-cake-btn">
              Request a Cake <ArrowRight size={16} strokeWidth={1.5} />
            </button>
            <button onClick={() => scrollToId("#work")} className="btn-outline" data-testid="hero-view-work-link">
              View Our Work
            </button>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 24px 24px)" }}
            animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 24px 24px)" }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="arch-frame relative mx-auto aspect-[3/4] max-w-sm overflow-hidden lg:max-w-none"
            data-testid="hero-image-frame"
          >
            <motion.img
              src="/images/hero.jpg"
              alt="Custom floral cake by Bella Bells Cakes, San Diego"
              style={{ y: imgY, scale: 1.18 }}
              className="h-full w-full object-cover"
              data-testid="hero-image"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            className="absolute -bottom-6 left-1/2 w-max -translate-x-1/2 rounded-2xl border border-[#E8D8D3] bg-white/95 px-6 py-4 shadow-sm backdrop-blur lg:-left-10 lg:translate-x-0"
            data-testid="hero-floating-card"
          >
            <p className="font-display text-lg italic text-[#2D2422]">Every cake, one of a kind</p>
            <p className="font-accent text-[11px] uppercase tracking-[0.2em] text-[#8A7975]">
              Designed around your celebration
            </p>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        onClick={() => scrollToId("#work")}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-[#8A7975] lg:block"
        aria-label="Scroll to gallery"
        data-testid="hero-scroll-indicator"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2 }} className="block">
          <ArrowDown size={20} strokeWidth={1.25} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
